import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check, AlertCircle } from "lucide-react";
import SockJS from "sockjs-client";
import { Client } from "@stomp/stompjs";
import axiosInstance from "../../axiosInstance";
import "./NotificationCenter.scss";
import { useAuth } from "../../contexts/AuthContext";
import notificationSound from "../../Sounds/notification.mp3";

// --- DEBUG FLAG ---
const DEBUG_MODE = false; // Set to true to enable debug logging

// Custom logger function that only logs when DEBUG_MODE is true.
const debugLog = (...args) => {
  if (DEBUG_MODE) {
    console.log("🔍 |", ...args);
  }
};

const NotificationCenter = ({ onNotificationReceived }) => {
  const { user, isLoading: isAuthLoading } = useAuth();

  const [notifications, setNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [error, setError] = useState(null);
  const [reconnecting, setReconnecting] = useState(false);
  const notificationDropdownRef = useRef(null);
  const stompClientRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptRef = useRef(0); // Track reconnection attempts
  const maxReconnectAttemptsRef = useRef(5); // Maximum reconnection attempts
  const initialDataFetchedRef = useRef(false);
  const readNotificationsRef = useRef(new Set()); // Track which notifications have been marked as read locally
  const lastNotificationIdRef = useRef(null); // To track the last processed notification
  const isRefreshingTokenRef = useRef(false); // Track token refresh status
  const isConnectingRef = useRef(false); // Track WebSocket connection status
  const connectionEstablishedRef = useRef(false); // Track if connection has been successfully established
  const processedNotificationIdsRef = useRef(new Set());
  const navigate = useNavigate();
  const audioRef = useRef(null);

  // Create the audio object once and preload it
  useEffect(() => {
    audioRef.current = new Audio(notificationSound);
    audioRef.current.preload = "auto";
    audioRef.current.volume = 0.7; // 0.0 - 1.0

    // Browsers block audio until the user has interacted with the page
    // (e.g. after a refresh). This "unlocks" it on the first click/keypress.
    const unlockAudio = () => {
      const audio = audioRef.current;
      if (!audio) return;
      audio
        .play()
        .then(() => {
          audio.pause();
          audio.currentTime = 0;
        })
        .catch(() => {});
      window.removeEventListener("click", unlockAudio);
      window.removeEventListener("keydown", unlockAudio);
    };
    window.addEventListener("click", unlockAudio);
    window.addEventListener("keydown", unlockAudio);

    return () => {
      window.removeEventListener("click", unlockAudio);
      window.removeEventListener("keydown", unlockAudio);
    };
  }, []);

  const playNotificationSound = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = 0; // restart if it's already playing
    audio.play().catch((err) => {
      // Autoplay can be blocked if the user hasn't interacted with the page yet
      console.warn("Notification sound was blocked:", err);
    });
  }, []);

  // Updated map notification types to sidebar sections
  const sectionMap = {
    // Original uppercase keys
    ATTENDANCE: "ess",
    PAYROLL: "compensation",
    PAYROLL_UPDATE: "compensation",
    PERFORMANCE_REVIEW: "performance",
    PERFORMANCE: "performance",
    ONBOARDING_STEP: "onboarding",
    ONBOARDING: "onboarding",
    OFFBOARDING: "offboarding",
    RECRUITMENT_STATUS: "recruitment",
    RECRUITMENT: "recruitment",
    TEAM_UPDATE: "teams",
    TEAM: "teams",
    SYSTEM_ADMIN: "admin",
    ADMIN: "admin",
    EMAIL_NOTIFICATION: "emailAutomation",
    EMAIL: "emailAutomation",

    // Adding support for regular case as shown in your data
    "Leave Request": "teams",
    "Leave Approval": "teams",
    Attendance: "ess",
    Payroll: "compensation",
    "Payroll Update": "compensation",
    "Performance Review": "performance",
    Performance: "performance",
    "Onboarding Step": "onboarding",
    Onboarding: "onboarding",
    Offboarding: "offboarding",
    "Recruitment Status": "recruitment",
    Recruitment: "recruitment",
    "Team Update": "teams",
    Team: "teams",
    "System Admin": "admin",
    Admin: "admin",
    "Email Notification": "emailAutomation",
    Email: "emailAutomation",

    // ADD THESE MISSING MAPPINGS FROM YOUR API DATA:
    "Re-Imbursement Request Submission": "teams",
    "Payroll Request Submission": "compensation",
    "Payroll Approval": "compensation",

    // Additional common notification types that might appear:
    REIMBURSEMENT_REQUEST: "teams",
    PAYROLL_REQUEST: "teams",
    EARLY_LOGOUT_REQUEST: "teams",
    LATE_LOGOUT_REQUEST: "teams",
    PAYROLL_APPROVAL: "compensation",
    LEAVE_REQUEST: "teams",
    LEAVE_APPROVAL: "teams",
    LEAVE_REJECTION: "teams",
    ATTENDANCE_ALERT: "ess",
    ATTENDANCE_REMINDER: "ess",
  };

  // Process notification counts for sidebar
  const processNotificationCounts = useCallback((data) => {
    if (!data) return {};

    const notificationCounts = {
      dashboard: 0,
      analytics: 0,
      emailAutomation: 0,
      ess: 0,
      recruitment: 0,
      onboarding: 0,
      performance: 0,
      compensation: 0,
      offboarding: 0,
      teams: 0,
      admin: 0,
    };

    // Process counts from API response format
    if (data.counts) {
      data.counts.forEach((item) => {
        // Check for type in both eventType and type fields
        const typeToUse = item.type || item.eventType;
        const section = sectionMap[typeToUse];

        if (section && notificationCounts[section] !== undefined) {
          notificationCounts[section] += item.count;
        }
      });
    }

    // Handle individual notifications if available
    if (data.notifications && data.notifications.length > 0) {
      // Count unread notifications by section
      const countBySection = {};

      data.notifications.forEach((notification) => {
        const typeToUse = notification.eventType || notification.type;
        const section = sectionMap[typeToUse];

        if (section && !notification.read) {
          countBySection[section] = (countBySection[section] || 0) + 1;
        }
      });

      // Merge with the existing counts
      Object.keys(countBySection).forEach((section) => {
        notificationCounts[section] += countBySection[section];
      });
    }

    return notificationCounts;
  }, []);

  // Get auth headers with fresh token
  const getAuthHeaders = useCallback(() => {
    const token = sessionStorage.getItem("jwtToken");
    const tenantId = sessionStorage.getItem("tenantId");

    if (!token || !tenantId) {
      // This is a warning we might want to see even in production.
      console.warn("Missing authentication information in sessionStorage");
      return {};
    }

    return {
      Authorization: token,
      "tenant-id": tenantId,
    };
  }, []);

  // New function to refresh token
  const refreshToken = useCallback(async () => {
    if (isRefreshingTokenRef.current) {
      // Already refreshing, wait for it to complete
      return new Promise((resolve) => {
        const checkRefreshComplete = setInterval(() => {
          if (!isRefreshingTokenRef.current) {
            clearInterval(checkRefreshComplete);
            resolve();
          }
        }, 100);
      });
    }

    isRefreshingTokenRef.current = true;
    debugLog("Refreshing JWT token for WebSocket connection...");

    try {
      const refreshResponse = await axiosInstance.post(
        `${process.env.REACT_APP_BACKEND_URL}/refresh-token`,
        {},
        { withCredentials: true },
      );

      const newJwtToken = refreshResponse.headers["authorization"];

      if (newJwtToken) {
        sessionStorage.setItem("jwtToken", newJwtToken);
        debugLog("Updated JWT token from refresh response headers.");
        isRefreshingTokenRef.current = false;
        return true;
      } else {
        console.warn("No JWT token found in refresh response headers");
        isRefreshingTokenRef.current = false;
        return false;
      }
    } catch (refreshError) {
      console.error("Error refreshing token:", refreshError);
      isRefreshingTokenRef.current = false;
      return false;
    }
  }, []);

  const notificationTypeToRouteMap = {
    // Leave related notifications
    LEAVE_REQUEST: "/team/manage-team-leave",
    LEAVE_APPROVAL: "/team/manage-team-leave",
    LEAVE_REJECTION: "/team/manage-team-leave",
    LEAVE_REVOKE_REQUEST: "/team/manage-team-leave",
    LEAVE_BALANCE_REMINDER: "/team/manage-team-leave",

    EARLY_LOGOUT_REQUEST: "/team/manage-attendance",

    // Payroll related notifications
    PAYROLL_REQUEST: "/compensation-benefits/payroll",
    PAYROLL_APPROVAL: "/compensation-benefits/payroll-report/salary-register",
    PAYROLL_REJECTION: "/compensation-benefits/payroll-report/salary-register",
    PAYSLIP_GENERATION: "/compensation-benefits/payroll-report/salary-register",

    // Reimbursement related notifications
    REIMBURSEMENT_REQUEST: "/team/team-reimbursements",
    REIMBURSEMENT_APPROVAL: "/team/team-reimbursements",
    REIMBURSEMENT_REJECTION: "/team/team-reimbursements",

    // Team related notifications
    TEAM_REMINDER: "/team/team-logs",

    // Generic reminders
    CUSTOM_REMINDER: "/dashboard",
    MANUAL_SCHEDULED: "/dashboard",
  };

  // Process notifications to include read status
  const processNotifications = useCallback((notificationsData) => {
    if (!notificationsData || !Array.isArray(notificationsData)) return [];

    return notificationsData.map((notification) => ({
      ...notification,
      // Consider a notification read if it's in our local read set
      read:
        notification.read || readNotificationsRef.current.has(notification.id),
    }));
  }, []);

  // First, add this to your component state/refs

  // Then modify your handleNewNotification function like this:
  const handleNewNotification = useCallback((data, source = "") => {
    // For debugging - helps identify where duplicates occur
    debugLog(`Processing notification from: ${source}`, data);

    // Safety check
    if (!data) return false;

    // Function to process a single notification
    // Fix for the handleNewNotification function in NotificationCenter.js

    // Replace the problematic toast.success call with this:
    const processAndToastNotification = (notif) => {
      if (!notif || !notif.id) return false;

      // Skip if we've already processed this notification
      if (processedNotificationIdsRef.current.has(notif.id)) {
        debugLog("Skipping already processed notification:", notif.id);
        return false;
      }

      // Mark as processed
      processedNotificationIdsRef.current.add(notif.id);
      lastNotificationIdRef.current = notif.id;

      return true;
    };

    // Case 1: Handle newNotification field
    if (data.newNotification) {
      return processAndToastNotification(data.newNotification);
    }

    // Case 2: Handle notifications array
    if (data.notifications && data.notifications.length > 0) {
      return processAndToastNotification(data.notifications[0]);
    }

    return false;
  }, []);

  // Reset connection state - helper function to ensure consistent state reset
  const resetConnectionState = useCallback(() => {
    if (stompClientRef.current && stompClientRef.current.connected) {
      try {
        debugLog("Deactivating STOMP client.");
        stompClientRef.current.deactivate();
      } catch (err) {
        console.error("Error deactivating STOMP client:", err);
      }
      stompClientRef.current = null;
    }

    isConnectingRef.current = false;
    connectionEstablishedRef.current = false;
  }, []);

  // Define handleConnectionError to be used for reconnection logic
  const handleConnectionError = useCallback(
    async (isTokenError = false) => {
      // Already reconnecting, avoid multiple reconnect attempts
      if (isConnectingRef.current && !isTokenError) {
        return;
      }

      setReconnecting(true);

      // Safety check - reset existing client if connected
      resetConnectionState();

      // Clear any existing timeout
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }

      // If exceeding max reconnect attempts, stop trying
      if (reconnectAttemptRef.current >= maxReconnectAttemptsRef.current) {
        console.error(
          `Maximum reconnection attempts (${maxReconnectAttemptsRef.current}) reached, giving up`,
        );
        setError("Connection failed after multiple attempts");
        setReconnecting(false);
        return;
      }

      reconnectAttemptRef.current++;

      // If it's a token error, try to refresh the token first
      if (isTokenError) {
        debugLog("Token error detected, attempting to refresh token");
        const refreshSuccess = await refreshToken();

        if (refreshSuccess) {
          // If token refresh was successful, reconnect immediately
          debugLog("Token refreshed successfully, reconnecting...");
          isConnectingRef.current = true;
          reconnectTimeoutRef.current = setTimeout(() => {
            setupWebSocketConnection();
          }, 1000); // Small delay to allow token to propagate
          return;
        } else {
          console.error("Token refresh failed, will retry connection later");
        }
      }

      // Exponential backoff for reconnect attempts
      const delay = Math.min(
        2000 * Math.pow(2, reconnectAttemptRef.current - 1),
        30000,
      );
      debugLog(
        `Attempting to reconnect in ${delay}ms (attempt ${reconnectAttemptRef.current})`,
      );

      // Attempt to reconnect after a delay
      reconnectTimeoutRef.current = setTimeout(() => {
        isConnectingRef.current = true;
        setupWebSocketConnection();
      }, delay);
    },
    [refreshToken, resetConnectionState],
  );

  // Fetch initial notifications via HTTP REST API
  const fetchInitialNotifications = useCallback(async () => {
    if (initialDataFetchedRef.current) return;

    if (initialDataFetchedRef.current) {
      debugLog("Initial data already fetched, skipping duplicate fetch");
      return;
    }

    debugLog("Fetching initial notifications via HTTP...");
    try {
      // Check if we have authentication info
      const token = sessionStorage.getItem("jwtToken");
      const tenantId = sessionStorage.getItem("tenantId");

      if (!token || !tenantId) {
        console.error("Missing authentication information");
        setError("Authentication information missing");
        return;
      }

      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_EMPLOYEE_URL}/notifications/unread-count`,
      );

      // Check for successful response (status 200)
      if (response && response.status === 200 && response.data) {
        debugLog("Successfully fetched initial notifications.", response.data);
        const data = response.data.data || response.data;

        handleNewNotification(data, "initialFetch");

        // Update the total unread count from the API response
        if (data.totalUnreadCount !== undefined) {
          setUnreadCount(data.totalUnreadCount);
        } else if (data.counts) {
          const totalUnread = data.counts.reduce(
            (total, item) => total + item.count,
            0,
          );
          setUnreadCount(totalUnread);
        }

        // Check for new notification to show toast
        handleNewNotification(data);

        // Process notifications if available, adding read status
        if (data.notifications) {
          const processedNotifications = processNotifications(
            data.notifications,
          );
          setNotifications(processedNotifications);
        }

        // Process counts for sidebar
        if (
          onNotificationReceived &&
          typeof onNotificationReceived === "function"
        ) {
          const notificationCounts = processNotificationCounts(data);
          onNotificationReceived(notificationCounts);
        }

        // Mark that we've fetched initial data
        initialDataFetchedRef.current = true;
        setError(null); // Clear any existing errors on successful fetch
      }
    } catch (err) {
      console.error("Error fetching initial notifications:", err);
      setError("Error fetching notifications");

      // Check if this was due to auth issues
      if (err.response && err.response.status === 401) {
        handleConnectionError(true);
      }
    }
  }, [
    onNotificationReceived,
    processNotificationCounts,
    handleConnectionError,
    processNotifications,
    handleNewNotification,
  ]);

  // WebSocket message handling function - moved outside to avoid recreation
  const handleWebSocketMessage = useCallback(
    (message) => {
      try {
        // Reset reconnection attempts on successful message
        reconnectAttemptRef.current = 0;

        // Try to parse the message body
        let messageData;
        try {
          messageData = JSON.parse(message.body);
        } catch (parseError) {
          console.error("Failed to parse message as JSON:", parseError);
          return;
        }

        debugLog("Received WebSocket message:", messageData);

        // First try the format with newNotification field
        if (messageData && messageData.newNotification) {
          // Update notifications state by adding the new notification to the top
          setNotifications((prevNotifications) => {
            // Check if this notification is already in the list
            const existingIndex = prevNotifications.findIndex(
              (n) => n.id === messageData.newNotification.id,
            );

            if (existingIndex >= 0) {
              // If it exists, return the current state
              return prevNotifications;
            }

            // Otherwise add it to the top of the list
            return [
              {
                ...messageData.newNotification,
                read: false,
              },
              ...prevNotifications,
            ];
          });

          // Update unread count
          setUnreadCount((prev) => prev + 1);

          // Show toast notification + play sound for brand-new notifications
          if (handleNewNotification(messageData, "websocket")) {
            playNotificationSound();
          }

          // Process counts for sidebar
          if (
            onNotificationReceived &&
            typeof onNotificationReceived === "function"
          ) {
            const notificationCounts = processNotificationCounts(messageData);
            onNotificationReceived(notificationCounts);
          }
        }
        // Handle the case with a notifications array
        else if (
          messageData &&
          messageData.notifications &&
          messageData.notifications.length > 0
        ) {
          // Create a set of existing notification IDs for quick lookup
          setNotifications((prevNotifications) => {
            const existingIds = new Set(prevNotifications.map((n) => n.id));
            const newNotifications = messageData.notifications.filter(
              (n) => !existingIds.has(n.id),
            );

            if (newNotifications.length === 0) {
              return prevNotifications;
            }

            // Add the new notifications to the top
            return [
              ...newNotifications.map((n) => ({ ...n, read: false })),
              ...prevNotifications,
            ];
          });

          // Update unread count if provided, otherwise increment by number of new notifications
          if (messageData.totalUnreadCount !== undefined) {
            setUnreadCount(messageData.totalUnreadCount);
          } else {
            setUnreadCount((prev) => prev + messageData.notifications.length);
          }

          // Show toast notification + play sound for brand-new notifications
          if (handleNewNotification(messageData, "websocket")) {
            playNotificationSound();
          }

          // Process counts for sidebar
          if (
            onNotificationReceived &&
            typeof onNotificationReceived === "function"
          ) {
            const notificationCounts = processNotificationCounts(messageData);
            onNotificationReceived(notificationCounts);
          }
        }
        // Try the original expected format with status and data fields
        else if (messageData.status !== undefined && messageData.data) {
          // Extract the data
          const data = messageData.data.data || messageData.data;

          // Process this data the same way as initial data
          if (data.notifications) {
            setNotifications((prevNotifications) => {
              const processedNotifications = processNotifications(
                data.notifications,
              );
              const existingIds = new Set(prevNotifications.map((n) => n.id));
              const newNotifications = processedNotifications.filter(
                (n) => !existingIds.has(n.id),
              );

              if (newNotifications.length === 0) {
                return prevNotifications;
              }

              return [...newNotifications, ...prevNotifications];
            });
          }

          // Update unread count
          if (data.totalUnreadCount !== undefined) {
            setUnreadCount(data.totalUnreadCount);
          } else if (data.counts) {
            const totalUnread = data.counts.reduce(
              (total, item) => total + item.count,
              0,
            );
            setUnreadCount(totalUnread);
          }

          // Show toast notification + play sound for brand-new notifications
          if (handleNewNotification(data, "websocket")) {
            playNotificationSound();
          }

          // Process counts for sidebar
          if (
            onNotificationReceived &&
            typeof onNotificationReceived === "function"
          ) {
            const notificationCounts = processNotificationCounts(data);
            onNotificationReceived(notificationCounts);
          }
        }
      } catch (err) {
        console.error("Error processing notification:", err);
        setError("Error processing notifications");
      }
    },
    [
      handleNewNotification,
      playNotificationSound,
      processNotifications,
      processNotificationCounts,
      onNotificationReceived,
    ],
  );

  // WebSocket connection with automatic reconnect on token refresh
  const setupWebSocketConnection = useCallback(() => {
    // Verify we have authentication info
    const token = sessionStorage.getItem("jwtToken");
    const tenantId = sessionStorage.getItem("tenantId");
    const username = user?.username || sessionStorage.getItem("username");

    if (!token || !tenantId || !username) {
      console.error("Authentication information missing for WebSocket.");
      setError("Authentication information missing");
      isConnectingRef.current = false;
      setReconnecting(false);
      return;
    }

    // Clear any existing timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Reset any existing connection
    resetConnectionState();

    try {
      isConnectingRef.current = true;
      setReconnecting(true);
      const webSocketURL = `${process.env.REACT_APP_BACKEND_URL}/websocket`;
      debugLog("Setting up WebSocket connection to:", webSocketURL);

      const socket = new SockJS(webSocketURL);

      const stompClient = new Client({
        webSocketFactory: () => socket,
        connectHeaders: getAuthHeaders(),
        debug: (str) => {
          // Log everything from the STOMP client in debug mode for max verbosity
          if (DEBUG_MODE) {
            debugLog("STOMP DEBUG:", str);
          }

          // Keep essential error checking for production
          if (
            str.includes("Invalid JWT token") ||
            str.includes("JWT token expired")
          ) {
            debugLog("STOMP detected token error.");
            stompClient.deactivate();
            handleConnectionError(true); // Pass true to indicate token error
          }
        },
        onConnect: () => {
          debugLog("Successfully connected to WebSocket.");
          setError(null);
          setReconnecting(false);
          isConnectingRef.current = false;
          connectionEstablishedRef.current = true;
          reconnectAttemptRef.current = 0; // Reset reconnect counter on successful connection

          // Subscribe to user-specific notification topic
          const topic = `/topic/${tenantId}/user/${username}/notifications`;
          debugLog("Subscribing to topic:", topic);

          try {
            stompClient.subscribe(topic, handleWebSocketMessage, {
              id: `notification-sub-${new Date().getTime()}`,
              ...getAuthHeaders(),
            });
            debugLog("Successfully subscribed to notifications topic.");
          } catch (subErr) {
            console.error("Error subscribing to topic:", subErr);
            setError("Subscription failed");
            handleConnectionError();
          }
        },
        onStompError: (frame) => {
          console.error("STOMP error:", frame.headers["message"], frame.body);
          setError("Connection error");
          isConnectingRef.current = false;
          connectionEstablishedRef.current = false;

          // Check if error is related to JWT token
          const errorMessage = frame.headers["message"] || "";
          const isTokenError =
            errorMessage.includes("Invalid JWT token") ||
            errorMessage.includes("JWT token expired") ||
            errorMessage.includes("Authorization");

          handleConnectionError(isTokenError);
        },
        onWebSocketClose: (evt) => {
          debugLog("WebSocket connection closed.", evt);
          isConnectingRef.current = false;
          connectionEstablishedRef.current = false;

          // Only trigger reconnect if this wasn't an intentional disconnect
          if (stompClientRef.current) {
            handleConnectionError();
          }
        },
        onWebSocketError: (evt) => {
          console.error("WebSocket error:", evt);
          setError("WebSocket error");
          isConnectingRef.current = false;
          connectionEstablishedRef.current = false;
          handleConnectionError();
        },
        reconnectDelay: 5000,
      });

      stompClient.activate();
      stompClientRef.current = stompClient;
    } catch (err) {
      console.error("Error setting up WebSocket:", err);
      setError("Connection error");
      isConnectingRef.current = false;
      connectionEstablishedRef.current = false;
      handleConnectionError();
    }
  }, [
    getAuthHeaders,
    handleConnectionError,
    handleWebSocketMessage,
    resetConnectionState,
    user?.username,
  ]);

  // Initialize component - fetch initial data via HTTP, then setup WebSocket
  useEffect(() => {
    // First fetch initial data via HTTP
    async function initializeNotificationCenter() {
      // Make sure we haven't already initialized
      if (!initialDataFetchedRef.current) {
        await fetchInitialNotifications();

        // If we successfully fetched initial data, setup WebSocket connection
        if (
          initialDataFetchedRef.current &&
          !connectionEstablishedRef.current
        ) {
          setupWebSocketConnection();
        }
      }
    }

    initializeNotificationCenter();

    // Cleanup function
    return () => {
      debugLog("NotificationCenter component unmounting. Cleaning up.");
      resetConnectionState();

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };
  }, [
    fetchInitialNotifications,
    setupWebSocketConnection,
    resetConnectionState,
  ]);

  // Mark a notification as read via HTTP API
  const markAsRead = useCallback(
    async (id, event) => {
      if (event) {
        event.stopPropagation();
      }

      debugLog(`Marking notification ${id} as read.`);
      try {
        processedNotificationIdsRef.current.add(id);

        // Call the API endpoint to mark a notification as read
        const response = await axiosInstance.put(
          `${process.env.REACT_APP_API_EMPLOYEE_URL}/notifications/${id}/read`,
        );

        if (response && response.status === 200) {
          // Add this notification ID to our local set of read notifications
          readNotificationsRef.current.add(id);

          // Find the notification before updating UI
          const notificationToUpdate = notifications.find((n) => n.id === id);

          // Optimistically update UI
          setNotifications((prev) =>
            prev.map((n) => (n.id === id ? { ...n, read: true } : n)),
          );

          // Decrement unread count
          setUnreadCount((prev) => Math.max(0, prev - 1));

          // Update sidebar notification counts
          if (
            onNotificationReceived &&
            typeof onNotificationReceived === "function" &&
            notificationToUpdate
          ) {
            const typeToUse =
              notificationToUpdate.eventType || notificationToUpdate.type;
            const section = sectionMap[typeToUse];

            if (section) {
              // Create a copy of the current notification counts
              const unreadNotifications = notifications.filter(
                (n) => n.id !== id && !n.read,
              );

              // Initialize counts object with zeros
              const updatedCounts = {
                dashboard: 0,
                analytics: 0,
                emailAutomation: 0,
                ess: 0,
                recruitment: 0,
                onboarding: 0,
                performance: 0,
                compensation: 0,
                offboarding: 0,
                teams: 0,
                admin: 0,
              };

              // Count remaining unread notifications by section
              unreadNotifications.forEach((notification) => {
                const nType = notification.eventType || notification.type;
                const nSection = sectionMap[nType];
                if (nSection && updatedCounts[nSection] !== undefined) {
                  updatedCounts[nSection] += 1;
                }
              });

              // Pass complete counts object to the sidebar
              onNotificationReceived(updatedCounts);
            }
          }

          return true; // Indicate success
        } else {
          console.error("Failed to mark notification as read:", response);
          setError("Failed to update notification");
          return false;
        }
      } catch (err) {
        console.error("Error marking notification as read:", err);
        setError("Error updating notification");

        // Check if this is an auth error and refresh token if needed
        if (err.response && err.response.status === 401) {
          await refreshToken();
        }
        return false;
      }
    },
    [notifications, onNotificationReceived, sectionMap, refreshToken],
  );

  // Mark all notifications as read via HTTP API
  const markAllAsRead = useCallback(async () => {
    debugLog("Marking all notifications as read.");
    try {
      // Call the API endpoint to mark all notifications as read
      const response = await axiosInstance.put(
        `${process.env.REACT_APP_API_EMPLOYEE_URL}/notifications/read-all`,
      );

      if (response && response.status === 200) {
        // Add all notification IDs to our local set of read notifications
        notifications.forEach((notification) => {
          readNotificationsRef.current.add(notification.id);
        });

        // Optimistically update UI
        setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
        setUnreadCount(0);

        // Reset all notification counts in sidebar
        if (
          onNotificationReceived &&
          typeof onNotificationReceived === "function"
        ) {
          const resetCounts = {
            dashboard: 0,
            analytics: 0,
            emailAutomation: 0,
            ess: 0,
            recruitment: 0,
            onboarding: 0,
            performance: 0,
            compensation: 0,
            offboarding: 0,
            teams: 0,
            admin: 0,
          };
          onNotificationReceived(resetCounts);
        }
      } else {
        console.error("Failed to mark all notifications as read:", response);
        setError("Failed to update notifications");
      }
    } catch (err) {
      console.error("Error marking all notifications as read:", err);
      setError("Error updating notifications");

      // Check if this is an auth error and refresh token if needed
      if (err.response && err.response.status === 401) {
        await refreshToken();
      }
    }
  }, [notifications, onNotificationReceived, refreshToken]);

  const navigateToSection = useCallback(
    async (notification) => {
      // Mark as read first if it's unread
      if (!notification.read) {
        try {
          await markAsRead(notification.id);
        } catch (error) {
          console.error("Failed to mark notification as read:", error);
          // Continue with navigation even if marking as read fails
        }
      }

      // Use either eventType or type, whichever is available
      const typeToUse = notification.eventType || notification.type;

      // Look up the route in the notificationTypeToRouteMap
      const route = notificationTypeToRouteMap[typeToUse] || "/dashboard"; // Default to dashboard if no mapping exists
      debugLog(
        `Navigating to route: ${route} for notification type: ${typeToUse}`,
      );

      // Use React Router's navigate function to go to the appropriate route
      navigate(route);

      // Close the notification dropdown
      setIsOpen(false);
    },
    [navigate, markAsRead], // Add markAsRead to the dependency array
  );

  // Format notification time
  const formatTime = useCallback((timestamp) => {
    if (!timestamp) return "";

    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return "Unknown time";

    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString();
  }, []);

  // Handle mouse enter event
  const handleMouseEnter = () => {
    setIsOpen(true);
  };

  // Handle mouse leave event
  const handleMouseLeave = () => {
    setIsOpen(false);
  };

  // Add manual reconnect capability
  const handleManualReconnect = useCallback(() => {
    debugLog("Manual reconnect triggered.");
    reconnectAttemptRef.current = 0; // Reset attempt counter
    initialDataFetchedRef.current = false; // Reset initial data flag
    fetchInitialNotifications(); // Re-fetch data
    // Connection will be established in the useEffect if needed
  }, [fetchInitialNotifications]);

  return (
    <div
      className="notification-center"
      ref={notificationDropdownRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="notification-bell" aria-label="Notifications">
        <Bell size={20} />
        {unreadCount > 0 && (
          <span
            className="notification-badge"
            aria-label={`${unreadCount} unread notifications`}
          >
            {unreadCount}
          </span>
        )}
      </div>

      {isOpen && (
        <div className="notification-dropdown">
          <div className="notification-header">
            <h3>Notifications</h3>
            {unreadCount > 0 && (
              <button
                className="mark-all-read"
                onClick={markAllAsRead}
                aria-label="Mark all as read"
              >
                Mark all as read
              </button>
            )}
          </div>

          <div className="notification-list">
            {reconnecting && (
              <div className="reconnecting-state">
                <span>Reconnecting...</span>
              </div>
            )}

            {error && !reconnecting && (
              <div className="error-state">
                <AlertCircle size={18} />
                <span>{error}</span>
                {reconnectAttemptRef.current >=
                  maxReconnectAttemptsRef.current && (
                  <button
                    className="retry-connection-btn"
                    onClick={handleManualReconnect}
                    aria-label="Retry connection"
                  >
                    Retry Connection
                  </button>
                )}
              </div>
            )}

            {!error && !reconnecting && notifications.length === 0 && (
              <div className="no-notifications">No notifications</div>
            )}

            {!error &&
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`notification-item ${
                    notification.read ? "" : "unread"
                  }`}
                  onClick={() => navigateToSection(notification)} // This will now only navigate without marking as read
                  role="button"
                  tabIndex={0}
                  aria-label={`Notification: ${
                    notification.header || notification.message
                  }`}
                >
                  <div className="notification-content">
                    <div className="notification-inner-header">
                      {notification.from || "System"}
                    </div>
                    <div className="notification-message">
                      {notification.message}
                    </div>
                    <div className="notification-time">
                      {formatTime(notification.timestamp)}
                    </div>
                  </div>
                  <div className="notification-actions">
                    {!notification.read && (
                      <button
                        className="mark-read-btn"
                        onClick={(e) => markAsRead(notification.id, e)}
                        title="Mark as read"
                        aria-label="Mark as read"
                      >
                        <Check size={16} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;
